import React, { useEffect, useMemo, useState } from "react";
import { Vote, Plus, Trash2, Trophy, CheckCircle2, Users, Settings2, Play, Square, FileText } from "lucide-react";
import { api, formatApiErrorDetail, fileViewUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { Badge2 } from "@/components/ComplianceBadge";
import { FileDropzone } from "@/components/FileDropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DEPTS = ["Usinagem", "Testes", "Pintura", "Instrumentacao", "Montagem", "Qualidade", "Caldeiraria", "Administrativo", "Geral"];

export default function Cipa() {
  const { canManage } = useAuth();
  const [data, setData] = useState({ config: {}, candidates: [], total_votes: 0, my_vote: null });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", department: "Geral", chapa: "", photo_id: null, photo_name: null });
  const [cfg, setCfg] = useState({ title: "", year: 2026, open: false, start_date: "", end_date: "" });
  const [delId, setDelId] = useState(null);
  const [voteId, setVoteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cipa");
      setData(data);
      setCfg({ title: data.config.title || "CIPA 2026", year: data.config.year || 2026, open: !!data.config.open, start_date: data.config.start_date || "", end_date: data.config.end_date || "" });
    } catch { toast.error("Falha ao carregar"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isOpen = !!data.config.open;
  const voted = !!data.my_vote;
  const maxVotes = useMemo(() => Math.max(1, ...data.candidates.map((c) => c.votes || 0)), [data.candidates]);

  const addCandidate = async () => {
    if (!form.name) { toast.error("Informe o nome"); return; }
    try { await api.post("/cipa/candidates", form); toast.success("Candidato cadastrado"); setAddOpen(false); setForm({ name: "", role: "", department: "Geral", chapa: "", photo_id: null, photo_name: null }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const downloadAta = async () => {
    try {
      const res = await api.get("/cipa/ata/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Ata_CIPA.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao gerar a ata"); }
  };
  const saveCfg = async () => {
    try { await api.put("/cipa/config", { ...cfg, year: Number(cfg.year || 2026) }); toast.success("Configuração salva"); setCfgOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleOpen = async () => {
    try { await api.put("/cipa/config", { ...cfg, open: !isOpen, year: Number(cfg.year || 2026) }); toast.success(!isOpen ? "Votação aberta" : "Votação encerrada"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doDelete = async () => { try { await api.delete(`/cipa/candidates/${delId}`); toast.success("Removido"); setDelId(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const doVote = async () => {
    try { await api.post("/cipa/vote", { candidate_id: voteId }); toast.success("Voto registrado! Obrigado por participar."); setVoteId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setVoteId(null); }
  };

  return (
    <div data-testid="cipa-page">
      <PageHeader icon={Vote} title={data.config.title || "CIPA — Eleição Eletrônica"} subtitle="Cadastro de candidatos, votação eletrônica e apuração automática">
        <Button variant="outline" size="sm" onClick={downloadAta} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="cipa-ata-btn"><FileText className="mr-1.5 h-4 w-4" /> Ata (PDF)</Button>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setCfgOpen(true)} className="border-slate-200 text-slate-600" data-testid="cipa-config-btn"><Settings2 className="mr-1.5 h-4 w-4" /> Configurar</Button>
        )}
        {canManage && (
          <Button size="sm" onClick={toggleOpen} className={isOpen ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"} data-testid="cipa-toggle-btn">
            {isOpen ? <><Square className="mr-1.5 h-4 w-4" /> Encerrar votação</> : <><Play className="mr-1.5 h-4 w-4" /> Abrir votação</>}
          </Button>
        )}
        {canManage && (
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="cipa-add-btn"><Plus className="mr-1.5 h-4 w-4" /> Candidato</Button>
        )}
      </PageHeader>

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <Badge2 color={isOpen ? "green" : "slate"}>{isOpen ? "Votação ABERTA" : "Votação ENCERRADA"}</Badge2>
        <span className="flex items-center gap-1.5 text-sm text-slate-600"><Users className="h-4 w-4 text-[#134074]" /> {data.total_votes} voto(s) computado(s)</span>
        <span className="text-sm text-slate-500">· {data.candidates.length} candidato(s)</span>
        {voted && <Badge2 color="blue">Você já votou</Badge2>}
        {!isOpen && data.candidates.length > 0 && data.candidates[0].votes > 0 && (
          <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-600"><Trophy className="h-4 w-4" /> Mais votado: {data.candidates[0].name}</span>
        )}
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Carregando...</div>
      ) : data.candidates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-white py-16 text-center text-slate-400">
          Nenhum candidato cadastrado ainda{canManage ? " — clique em 'Candidato' para começar." : "."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.candidates.map((c, i) => {
            const isMine = data.my_vote === c.id;
            const pct = data.total_votes ? Math.round((c.votes / data.total_votes) * 100) : 0;
            return (
              <div key={c.id} className={`relative overflow-hidden rounded-xl border bg-white p-5 shadow-sm transition-all hover:shadow-md ${isMine ? "border-emerald-400 ring-1 ring-emerald-300" : "border-[#E2E8F0]"}`} data-testid={`cipa-card-${c.id}`}>
                {i === 0 && c.votes > 0 && <div className="absolute right-3 top-3"><Trophy className="h-5 w-5 text-amber-500" /></div>}
                <div className="flex items-center gap-3">
                  {c.photo_id ? (
                    <img src={fileViewUrl(c.photo_id)} alt={c.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-[#0B2545]/10" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0B2545] text-lg font-bold text-white">{c.name.charAt(0).toUpperCase()}</div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[#0B2545]">{c.name}</p>
                    <p className="truncate text-xs text-slate-500">{c.role || "—"} · {c.department}</p>
                  </div>
                </div>
                {c.chapa && <Badge2 color="blue" className="mt-2">Chapa: {c.chapa}</Badge2>}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-slate-500"><span>{c.votes} voto(s)</span><span className="font-semibold text-[#134074]">{pct}%</span></div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-[#134074] to-emerald-500 transition-all" style={{ width: `${data.total_votes ? Math.round((c.votes / maxVotes) * 100) : 0}%` }} /></div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {isMine ? (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Seu voto</span>
                  ) : (
                    <Button size="sm" disabled={!isOpen || voted} onClick={() => setVoteId(c.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" data-testid={`cipa-vote-${c.id}`}>
                      <Vote className="mr-1.5 h-4 w-4" /> Votar
                    </Button>
                  )}
                  {canManage && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDelId(c.id)} data-testid={`cipa-del-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="cipa-add-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Novo candidato</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs font-semibold text-slate-600">Nome *</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="cipa-name" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Cargo</Label><Input value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Setor</Label>
              <Select value={form.department} onValueChange={(v) => setForm((p) => ({ ...p, department: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs font-semibold text-slate-600">Chapa (opcional)</Label><Input value={form.chapa} onChange={(e) => setForm((p) => ({ ...p, chapa: e.target.value }))} /></div>
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs font-semibold text-slate-600">Foto do candidato</Label>
              <FileDropzone value={form.photo_id} valueName={form.photo_name} onUploaded={({ id, name }) => setForm((p) => ({ ...p, photo_id: id, photo_name: name }))} testid="cipa-photo" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button><Button onClick={addCandidate} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="cipa-add-save">Cadastrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="cipa-config-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Configurar eleição</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs font-semibold text-slate-600">Título</Label><Input value={cfg.title} onChange={(e) => setCfg((p) => ({ ...p, title: e.target.value }))} data-testid="cipa-cfg-title" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Ano</Label><Input type="number" value={cfg.year} onChange={(e) => setCfg((p) => ({ ...p, year: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Início</Label><Input type="date" value={cfg.start_date || ""} onChange={(e) => setCfg((p) => ({ ...p, start_date: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Fim</Label><Input type="date" value={cfg.end_date || ""} onChange={(e) => setCfg((p) => ({ ...p, end_date: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCfgOpen(false)}>Cancelar</Button><Button onClick={saveCfg} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="cipa-cfg-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remover candidato</AlertDialogTitle><AlertDialogDescription>Os votos deste candidato também serão removidos.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!voteId} onOpenChange={(o) => !o && setVoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Confirmar voto</AlertDialogTitle><AlertDialogDescription>Você poderá votar apenas uma vez nesta eleição. Deseja confirmar?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doVote} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cipa-vote-confirm">Confirmar voto</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
